export type SourceType = 'ADMS_PUSH' | 'BIOTIME_REST' | 'SQL_DATABASE' | 'CSV_IMPORT'
export type SqlDialect = 'BIOTIME_8' | 'ZKTIME_LEGACY' | 'MAPPED' | 'CUSTOM'
export type DbVendor = 'POSTGRESQL' | 'SQLSERVER' | 'MYSQL'
export type WatermarkMode = 'TIMESTAMP' | 'ID'
export type IdentityMatchMode = 'DEVICE_PIN' | 'BADGE_NUMBER' | 'EMPLOYEE_CODE' | 'CUSTOM_FIELD'

/**
 * What each identity mode reads, in the words an administrator configuring a BioTime
 * install would use. The distinction matters and is easy to get wrong: BioTime's emp_code
 * is the staff number from its own personnel records, not the PIN enrolled on a terminal,
 * and choosing the wrong one imports every punch as unmatched while looking like it worked.
 */
export const IDENTITY_MATCH_LABELS: Record<IdentityMatchMode, string> = {
  DEVICE_PIN: 'Device PIN — the number enrolled on the terminal',
  BADGE_NUMBER: 'Badge number — the card the employee taps',
  EMPLOYEE_CODE: 'Employee code — what BioTime calls emp_code',
  CUSTOM_FIELD: 'A custom field on the employee record',
}

export interface DeviceView {
  id: string
  serialNumber: string
  name: string
  model: string | null
  firmware: string | null
  ipAddress: string | null
  status: 'ACTIVE' | 'DISABLED' | 'PENDING'
  protocol: string
  online: boolean
  lastSeenAt: string | null
  lastSyncAt: string | null
  userCount: number | null
  attendanceLogCount: number | null
  locationId: string | null
}

export interface PunchView {
  id: string
  deviceId: string
  serialNumber: string
  devicePin: string
  employeeId: string | null
  employeeName: string | null
  punchTime: string
  verifyType: number | null
  punchState: number | null
  processStatus: 'PENDING' | 'PROCESSED' | 'UNMATCHED' | 'IGNORED'
  receivedAt: string
}

export interface SourceView {
  id: string
  name: string
  type: SourceType
  enabled: boolean
  pollIntervalMinutes: number
  watermark: string | null
  lastRunAt: string | null
  lastRunStatus: string | null
  lastRunMessage: string | null
  lastRunImported: number | null
  sqlDialect: SqlDialect | null
  jdbcUrl: string | null
  databaseUsername: string | null
  apiBaseUrl: string | null
  apiUsername: string | null
  /** True when a secret is stored. The secret itself is never returned. */
  hasCredential: boolean

  // Schema mapping. ZKTeco publishes no database schema, so every identifier the connector
  // would otherwise hard-code is a field an administrator can override per install.
  dbVendor: DbVendor | null
  dbSchema: string | null
  sourceTable: string | null
  sourceJoin: string | null
  columnId: string | null
  columnPin: string | null
  columnPunchTime: string | null
  columnPunchState: string | null
  columnVerifyType: string | null
  columnDeviceSn: string | null
  columnWorkCode: string | null
  watermarkMode: WatermarkMode
  watermarkId: number | null
  identityMatchMode: IdentityMatchMode
  identityMatchField: string | null
  batchSize: number
  lastReplayAt: string | null
}

/**
 * One sync attempt.
 *
 * Rows read against rows inserted is the whole value of this: it is what tells a replay
 * that found nothing new apart from a source that is not returning anything at all.
 */
export interface SourceRunView {
  id: string
  sourceId: string
  trigger: 'SCHEDULED' | 'MANUAL' | 'REPLAY'
  status: 'RUNNING' | 'OK' | 'FAILED' | 'SKIPPED'
  startedAt: string
  finishedAt: string | null
  rowsRead: number
  rowsInserted: number
  rowsDuplicate: number
  rowsUnmatched: number
  watermarkBefore: string | null
  watermarkAfter: string | null
  replayFrom: string | null
  replayTo: string | null
  message: string | null
}

export type RegularizationType =
  | 'MISSING_IN'
  | 'MISSING_OUT'
  | 'MISSING_BOTH'
  | 'WRONG_TIME'
  | 'OTHER'

export type RegularizationOrigin = 'SYSTEM' | 'EMPLOYEE' | 'MANAGER'

export type RegularizationStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'

export const REGULARIZATION_TYPE_LABELS: Record<RegularizationType, string> = {
  MISSING_IN: 'No time in recorded',
  MISSING_OUT: 'No time out recorded',
  MISSING_BOTH: 'Worked, but nothing recorded',
  WRONG_TIME: 'A recorded time is wrong',
  OTHER: 'Something else',
}

export interface RegularizationRequestView {
  id: string
  employeeId: string
  employeeName: string
  workDate: string
  assignmentId: string | null
  requestType: RegularizationType
  origin: RegularizationOrigin
  status: RegularizationStatus
  requestedFirstIn: string | null
  requestedLastOut: string | null
  priorFirstIn: string | null
  priorLastOut: string | null
  priorStatus: string | null
  priorWorkedMinutes: number | null
  reason: string | null
  workflowInstanceId: string | null
  appliedAt: string | null
  decidedAt: string | null
  decisionComment: string | null
  attachmentFileIds: string[]
}

/** A punch as the terminal recorded it, shown next to the time being claimed. */
export interface RegularizationPunchView {
  punchTime: string
  punchState: number | null
  deviceSerial: string | null
  source: string
}

export interface RegularizationDecisionContext {
  request: RegularizationRequestView
  punches: RegularizationPunchView[]
  currentWorkedMinutes: number | null
  currentStatus: string | null
  workDate: string
  shiftName: string | null
  shiftStart: string | null
  shiftEnd: string | null
  holiday: boolean
  weekOff: boolean
  myTaskId: string | null
}

export type OrgGroupBy = 'SITE' | 'ESTABLISHMENT' | 'DEPARTMENT' | 'ENTITY'

export const ORG_GROUP_LABELS: Record<OrgGroupBy, string> = {
  SITE: 'Site',
  ESTABLISHMENT: 'Establishment',
  DEPARTMENT: 'Department',
  ENTITY: 'Legal entity',
}

export interface OrgAttendanceRow {
  groupId: string | null
  groupName: string
  employees: number
  days: number
  presentDays: number
  absentDays: number
  leaveDays: number
  workedMinutes: number
  overtimeMinutes: number
  lateMinutes: number
}

/** One approval waiting on the signed-in user. */
export interface ApprovalTask {
  taskId: string
  instanceId: string
  entityKey: string | null
  entityId: string | null
  title: string | null
  stepName: string | null
  stepOrder: number
  status: string
  dueAt: string | null
  assignedAt: string | null
}

export interface TestResult {
  success: boolean
  message: string
  sampleCount: number | null
}

/** How the device reported the identity — the ZKTeco verify-mode scale. */
export const VERIFY_TYPES: Record<number, string> = {
  0: 'Password',
  1: 'Fingerprint',
  2: 'Card',
  3: 'Password',
  4: 'Card',
  15: 'Face',
  25: 'Palm',
}

/** Which way through the door. */
export const PUNCH_STATES: Record<number, string> = {
  0: 'Check in',
  1: 'Check out',
  2: 'Break out',
  3: 'Break in',
  4: 'Overtime in',
  5: 'Overtime out',
}

export interface ShiftView {
  id: string
  code: string
  name: string
  startTime: string
  endTime: string
  crossesMidnight: boolean
  graceInMinutes: number
  breakMinutes: number
  fullDayMinutes: number
  active: boolean
  assignedCount: number
}

export interface RosterView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  shiftId: string
  shiftName: string | null
  fromDate: string
  toDate: string | null
  weekOffDays: string[]
}

export interface HolidayView {
  id: string
  holidayDate: string
  name: string
  /** A day people may take but need not — still a working day unless leave is booked. */
  optional: boolean
  /** Null means the whole company observes it. */
  locationId: string | null
  locationName: string | null
}

export interface ProcessingResult {
  from: string
  to: string
  employeesProcessed: number
  daysWritten: number
  punchesConsumed: number
}

/**
 * Week starts on Monday here regardless of locale.
 *
 * The backend normalises every timesheet and roster week to a Monday, so a UI that
 * started the week on Sunday would show a person's Sunday hours under the wrong sheet.
 */
export const WEEK_DAYS = [
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY',
] as const

export type WeekDay = (typeof WEEK_DAYS)[number]

export function shortDayName(day: string): string {
  return day.charAt(0) + day.slice(1, 3).toLowerCase()
}
